<template>
  <div class="container">
    <form novalidate @submit.prevent="submitEditProfile">
      <BreadcrumbGroup :items="breadcrumbItems" />

      <HeadingOne>お客さま情報の変更</HeadingOne>

      <ErrorsAlert
        :errors="errors"
        :column-only="true"
        :items="inputItemsMb"
      ></ErrorsAlert>

      <WizardGroup class="_mb-md-60 _mb-sm-20">
        <MainWizard :active-index="0" type="userinfo"></MainWizard>
      </WizardGroup>

      <p>変更内容をご入力のうえ、「確認画面へ」を押してください。</p>

      <section>
        <TableGroup>
          <HeadingTwo class="_mt-50">お客さま情報</HeadingTwo>
          <TableBox>
            <TableBody>
              <TableRow>
                <TableHeader> お客さま番号 </TableHeader>
                <TableData>
                  {{ userData.account_id }}
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader>
                  氏名<small>（漢字）</small>
                  <TooltipsItem placement="righttop" clazz="question-box">
                    ご結婚などにより改姓した場合は、コールセンターへご連絡ください。
                  </TooltipsItem>
                </TableHeader>
                <TableData>
                  <label class="name">{{ userData.lastname_kanji }}</label>
                  <label class="name">{{ userData.firstname_kanji }}</label>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader>
                  氏名<small>（フリガナ）</small>
                  <TooltipsItem placement="righttop" clazz="question-box">
                    ご結婚などにより改姓した場合は、コールセンターへご連絡ください。
                  </TooltipsItem>
                </TableHeader>
                <TableData>
                  <label class="name">{{ userData.lastname_kana }}</label>
                  <label class="name">{{ userData.firstname_kana }}</label>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader> 生年月日 </TableHeader>
                <TableData>
                  {{ displayBth(userData.bth) }}
                </TableData>
              </TableRow>
              <!--<TableRow>
                <TableHeader>
                  性別
                </TableHeader>
                <TableData>
                  {{displaySex}}
                </TableData>
              </TableRow>-->
              <TableRow v-if="auUser(userData.id_type) && hasPhoneAu">
                <TableHeader>
                  au携帯電話番号または<br />UQ mobile 電話番号
                </TableHeader>
                <TableData>
                  {{
                    displayPhoneAu(
                      userData.phone_au_1,
                      userData.phone_au_2,
                      userData.phone_au_3
                    )
                  }}
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader>
                  電話番号<small
                    >（その他）<small class="moji">【半角数字】</small></small
                  >
                </TableHeader>
                <TableData :has-error="errors['phone_mobile']">
                  <InputGroup :errors="errors" :prop-names="['phone_mobile']">
                    <InputgroupAppend>
                      <InputPhone
                        id="phone_mobile"
                        :phone="{
                          phone1: userData.phone_mobile_1,
                          phone2: userData.phone_mobile_2,
                          phone3: userData.phone_mobile_3,
                        }"
                        :is-mobile="true"
                        @input="setPhone"
                      ></InputPhone>
                    </InputgroupAppend>
                    <InputLabel x-ms-format-detection="none">
                      入力例： 090-****-****
                    </InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader> 毎月のご返済日 </TableHeader>
                <TableData>
                  毎月{{ userData.contractual_repayment_cycle }}日
                </TableData>
              </TableRow>
            </TableBody>
          </TableBox>

          <HeadingTwo class="_mt-50">ご自宅情報</HeadingTwo>
          <TableBox>
            <TableBody>
              <TableRow>
                <TableHeader colspan="2">
                  郵便番号<small class="moji">【半角数字※ハイフン不要】</small>
                </TableHeader>
                <TableData :has-error="errors['postcode']">
                  <InputGroup :errors="errors" :prop-names="['postcode']">
                    <InputgroupAppend>
                      <InputItem
                        id="postcode"
                        v-model="userData.postcode"
                        type="text"
                        class="-md"
                        :maxlength="7"
                      ></InputItem>
                    </InputgroupAppend>
                    <InputLabel>入力例：1234567</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader class="-harfleft" rowspan="4">
                  ご住所<small>（漢字）</small>
                </TableHeader>
                <TableHeader class="-harfright">
                  都道府県<small class="moji">【全角】</small>
                </TableHeader>
                <TableData :has-error="errors['address_region']">
                  <InputGroup :errors="errors" :prop-names="['address_region']">
                    <InputGroup>
                      <InputItem
                        id="address_region"
                        v-model="userData.address_region"
                        type="text"
                        class="-md"
                        :maxlength="60"
                      ></InputItem>
                      <InputLabel>入力例：東京都</InputLabel>
                    </InputGroup>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader class="-harfright">
                  市区町村<small class="moji">【全角】</small>
                </TableHeader>
                <TableData :has-error="errors['address_city']">
                  <InputGroup :errors="errors" :prop-names="['address_city']">
                    <InputItem
                      id="address_city"
                      v-model="userData.address_city"
                      class="-lg"
                      :maxlength="60"
                    ></InputItem>
                    <InputLabel>入力例：港区</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader class="-harfright">
                  地名・番地以下<small class="moji">【全角】</small>
                </TableHeader>
                <TableData :has-error="errors['address_street']">
                  <InputGroup :errors="errors" :prop-names="['address_street']">
                    <InputItem
                      id="address_street"
                      v-model="userData.address_street"
                      class="-lg"
                      :maxlength="60"
                    ></InputItem>
                    <InputLabel>入力例：○○○１－１ー１</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader class="-harfright"
                  >建物名・部屋番号<small class="moji"
                    >【全角】</small
                  ></TableHeader
                >
                <TableData :has-error="errors['address_building']">
                  <InputGroup
                    :errors="errors"
                    :prop-names="['address_building']"
                  >
                    <InputItem
                      id="address_building"
                      v-model="userData.address_building"
                      class="-lg"
                      :maxlength="60"
                    ></InputItem>
                    <InputLabel>入力例：○○マンション１０１号室</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader colspan="2">
                  ご住所<small
                    >（フリガナ）<small class="moji"
                      >【全角カナ・全角英数】</small
                    ></small
                  >
                </TableHeader>
                <TableData :has-error="errors['address_kana']">
                  <InputGroup :errors="errors" :prop-names="['address_kana']">
                    <TextArea
                      id="address_kana1"
                      v-model="userData.address_kana"
                      class="furigana"
                      :maxlength="166"
                      exclude-line-code="true"
                    ></TextArea>
                    <InputLabel
                      >入力例：トウキョウトミナトク○○○１－１－１○○マンション１０１ゴウシツ<br />※スペースを空けず、つめてご入力ください。</InputLabel
                    >
                  </InputGroup>
                </TableData>
              </TableRow>
            </TableBody>
            <!-- table-body -->
          </TableBox>
          <TableBox>
            <TableBody>
              <TableRow>
                <TableHeader>お住まいの種類</TableHeader>
                <TableData>
                  <BtngroupRadio>
                    <BtnRadio
                      v-for="(homeType, index) in home_types"
                      :id="'homeType' + index"
                      :key="'homeType' + index"
                      v-model="userData.home_type"
                      name="homeTypeRadio"
                      :value="homeType['value']"
                      :label="homeType['name']"
                    >
                    </BtnRadio>
                  </BtngroupRadio>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader>
                  居住開始年月<small class="moji">【半角数字】</small>
                </TableHeader>
                <TableData :has-error="errors['nky_ngt']">
                  <InputGroup :errors="errors" :prop-names="['nky_ngt']">
                    <InputgroupAppend>
                      <InputItem
                        id="nkyNgtYyyy"
                        v-model="nkyNgtYyyy"
                        type="text"
                        class="-xs"
                        :maxlength="4"
                        @change="setNkyNgt"
                      ></InputItem>
                      <InputAppend>年</InputAppend>
                      <InputItem
                        id="nkyNgtMm"
                        v-model="nkyNgtMm"
                        type="text"
                        class="-xs"
                        :maxlength="2"
                        @change="setNkyNgt"
                      ></InputItem>
                      <InputAppend>月~</InputAppend>
                    </InputgroupAppend>
                    <InputLabel>入力例：2019年3月～</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader
                  >住宅ローンもしくは家賃のお支払い<small
                    >(月額)<small class="moji">【半角数字】</small></small
                  >
                  <TooltipsItem placement="righttop">
                    ご自身のご負担がない場合は「0」を入力してください。
                  </TooltipsItem>
                </TableHeader>
                <TableData :has-error="errors['rent_loan_amount']">
                  <InputGroup
                    :errors="errors"
                    :prop-names="['rent_loan_amount']"
                  >
                    <InputgroupAppend>
                      <InputItem
                        id="housingLoan"
                        v-model="userData.rent_loan_amount"
                        type="text"
                        class="-sm"
                        :maxlength="10"
                      ></InputItem>
                      <InputAppend>円／月</InputAppend>
                    </InputgroupAppend>
                    <InputLabel>入力例：75000</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>
            </TableBody>
          </TableBox>
          <TableBox>
            <TableBody>
              <TableRow>
                <TableHeader>ご家族<small>（配偶者さま）</small></TableHeader>
                <TableData>
                  <BtngroupRadio>
                    <BtnRadio
                      v-for="(marriageStatus, index) in marriage_statuses"
                      :id="'marriageStatus' + index"
                      :key="'marriageStatus' + index"
                      v-model="userData.marriage_status"
                      name="marriageStatus"
                      :value="marriageStatus['value']"
                      :label="marriageStatus['name']"
                    >
                    </BtnRadio>
                  </BtngroupRadio>
                </TableData>
              </TableRow>
              <TableRow>
                <TableHeader>ご家族<small>（お子さま）</small></TableHeader>
                <TableData>
                  <BtngroupRadio>
                    <BtnRadio
                      v-for="(numberOfChildren, index) in number_of_children"
                      :id="'numberOfChildren' + index"
                      :key="'numberOfChildren' + index"
                      v-model="userData.number_of_children"
                      name="numberOfChildren"
                      :value="numberOfChildren['value']"
                      :label="numberOfChildren['name']"
                    >
                    </BtnRadio>
                  </BtngroupRadio>
                </TableData>
              </TableRow>
            </TableBody>
            <!-- table-body -->
          </TableBox>
        </TableGroup>

        <BtngroupDouble class="-margin-lg">
          <template #main>
            <BtnMain class="-main" @click.prevent="submitEditProfile">
              確認画面へ
            </BtnMain>
          </template>
          <template #back>
            <BtnBack class="-back -sm" router-name="mb023"> 戻る </BtnBack>
          </template>
        </BtngroupDouble>
      </section>
    </form>
  </div>
</template>

<script>
import { initCredentials } from "@/utils/Auth";
import {
  getUserDataForMemberLambdaInvoke,
  editProfileLambdaInvoke,
} from "@/utils/LambdaHelper";
import {
  homeTypes,
  marriageStatuses,
  numberOfChildren,
  inputItemsMb,
} from "@/constants/constant";
import { setMeta } from "@/utils/Meta";
import { goToErrorPage, scrollTop } from "@/utils/Other";
import {
  setNkyNgtYyyy,
  setNkyNgtMm,
  setHasPhoneAu,
  setHasPhoneMobile,
  setPhoneMobile,
  setNkyNgt,
  displayBth,
  auUser,
  displayPhoneAu,
} from "@/utils/UserData";

import TableGroup from "@/components/tables/TableGroup";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import HeadingOne from "@/components/heading/HeadingOne";
import HeadingTwo from "@/components/heading/HeadingTwo";
import TooltipsItem from "@/components/tooltip/TooltipsItem";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";
import BtnRadio from "@/components/button/BtnRadio";
import BtngroupRadio from "@/components/button/BtngroupRadio";
import BtngroupDouble from "@/components/button/BtngroupDouble";
import MainWizard from "@/components/wizard/MainWizard";
import WizardGroup from "@/components/wizard/WizardGroup";
import InputGroup from "@/components/input/InputGroup";
import InputItem from "@/components/input/InputItem";
import InputgroupAppend from "@/components/input/InputgroupAppend";
import InputAppend from "@/components/input/InputAppend";
import InputLabel from "@/components/input/InputLabel";
import InputPhone from "@/components/input/InputPhone";
import ErrorsAlert from "@/components/alert/ErrorsAlert";
import TextArea from "@/components/input/TextArea";
import BreadcrumbGroup from "@/components/breadcrumb/BreadcrumbGroup";

export default {
  name: "Mb024",
  components: {
    TextArea,
    ErrorsAlert,
    InputPhone,
    TableGroup,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    HeadingOne,
    HeadingTwo,
    TooltipsItem,
    BtnMain,
    BtnBack,
    BtnRadio,
    BtngroupRadio,
    BtngroupDouble,
    WizardGroup,
    MainWizard,
    InputGroup,
    InputItem,
    InputgroupAppend,
    InputAppend,
    InputLabel,
    BreadcrumbGroup,
  },
  setup() {
    setMeta({
      title: "お客さま情報の変更｜お客さま情報の確認・変更",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,会員,会員サイト,お客さま情報",
    });

    return {
      setNkyNgtYyyy,
      setNkyNgtMm,
      setHasPhoneAu,
      setHasPhoneMobile,
      setPhoneMobile,
      setNkyNgt,
      displayBth,
      auUser,
      displayPhoneAu,
    };
  },
  data() {
    return {
      breadcrumbItems: [
        {
          text: "トップ",
          routerName: "mb001",
        },
        {
          text: "お客さま情報の確認・変更",
          routerName: "mb023",
        },
        {
          text: "お客さま情報の変更",
          active: true,
        },
      ],
      errors: {},
      home_types: homeTypes,
      marriage_statuses: marriageStatuses,
      number_of_children: numberOfChildren,
      hasPhoneMobile: false,
      hasPhoneAu: false,
      userData: {
        id_type: "",
        account_id: "",
        firstname_kanji: "",
        lastname_kanji: "",
        firstname_kana: "",
        lastname_kana: "",
        bth: "",
        sex: "",
        phone_au_1: "",
        phone_au_2: "",
        phone_au_3: "",
        phone_mobile_1: "",
        phone_mobile_2: "",
        phone_mobile_3: "",
        repayment_date: "",
        contractual_repayment_cycle: "",
        postcode: "",
        address_region: "",
        address_city: "",
        address_street: "",
        address_building: "",
        address_kana: "",
        home_type: "",
        nky_ngt: "",
        rent_loan_amount: "",
        marriage_status: "",
        number_of_children: "",
      },
      isDisabled: false,
      nkyNgtYyyy: "",
      nkyNgtMm: "",
      inputItemsMb: inputItemsMb,
    };
  },
  mounted() {
    try {
      getUserDataForMemberLambdaInvoke()
        .then((res) => {
          for (let key in res) {
            if (Object.prototype.hasOwnProperty.call(this.userData, key)) {
              this.userData[key] = res[key].toString().trim();
            }
          }
          this.nkyNgtYyyy = setNkyNgtYyyy(this.userData.nky_ngt);
          this.nkyNgtMm = setNkyNgtMm(this.userData.nky_ngt);
          this.hasPhoneAu = setHasPhoneAu(
            this.userData.phone_au_1,
            this.userData.phone_au_2,
            this.userData.phone_au_3
          );
          this.hasPhoneMobile = setHasPhoneMobile(
            this.userData.phone_mobile_1,
            this.userData.phone_mobile_2,
            this.userData.phone_mobile_3
          );
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    } catch (e) {
      goToErrorPage(e);
    }
  },
  methods: {
    setPhone(data) {
      this.userData = {
        ...this.userData,
        ...setPhoneMobile(data.phone1, data.phone2, data.phone3),
      };
    },
    submitEditProfile() {
      try {
        if (this.isDisabled) {
          return;
        }
        this.isDisabled = true;
        this.errors = {};
        this.userData.nky_ngt = setNkyNgt(this.nkyNgtYyyy, this.nkyNgtMm);
        initCredentials()
          .then(() => {
            editProfileLambdaInvoke(this.userData)
              .then((res) => {
                this.errors = res.errors || {};
                if (Object.keys(this.errors).length > 0) {
                  scrollTop();
                  this.isDisabled = false;
                } else {
                  this.$router.push({ name: "mb027" });
                }
              })
              .catch((error) => {
                goToErrorPage(error);
              });
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";
.table-data {
  vertical-align: middle;
}
.furigana {
  font-family: inherit;
}
</style>
